import { useMutation } from '@apollo/client'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'

import {
  IEditPrepSettingsScheduleData,
  IEditPrepSettingsScheduleVariables,
  IScheduleInput,
  IScopeOption,
  editPrepSettingsSchedule,
} from '../gql'
import { ISettingSchedule } from '../hooks/usePrepSettings'
import RuleRows, { IEditableRule } from './RuleRows'
import validateRules from './validateRules'

interface IEditableSchedule {
  id: string
  startDate: string
  endDate: string
  rules: IEditableRule[]
}

interface IEditPrepSettingsScheduleModalProps {
  visible: boolean
  onClose: () => void
  settingName: string
  currentSchedules: ISettingSchedule[]
  scopes: IScopeOption[]
  scopesLoading: boolean
  scopesError: boolean
  onSuccess?: () => void
}

const generateId = () => Math.random().toString(36).substring(2, 9)

const EditPrepSettingsScheduleModal = ({
  visible,
  onClose,
  settingName,
  currentSchedules,
  scopes,
  scopesLoading,
  scopesError,
  onSuccess,
}: IEditPrepSettingsScheduleModalProps) => {
  const [schedules, setSchedules] = useState<IEditableSchedule[]>([])
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const initialSchedulesRef = useRef<string>('')

  const [editPrepSettingsScheduleMutation] = useMutation<
    IEditPrepSettingsScheduleData,
    IEditPrepSettingsScheduleVariables
  >(editPrepSettingsSchedule)

  useEffect(() => {
    if (visible) {
      const toEditable = (s: ISettingSchedule) => ({
        id: generateId(),
        startDate: s.startDate,
        endDate: s.endDate,
        rules: s.rules.map((rule) => ({
          id: generateId(),
          locationGroupId: rule.locationGroupId,
          value: rule.value.toString(),
        })),
      })
      setSchedules(currentSchedules.map(toEditable))
      initialSchedulesRef.current = JSON.stringify(
        currentSchedules.map((s) => ({
          startDate: s.startDate,
          endDate: s.endDate,
          rules: s.rules.map((rule) => [
            rule.locationGroupId,
            rule.value.toString(),
          ]),
        })),
      )
      setError(null)
      setSuccess(false)
    }
  }, [visible, currentSchedules])

  const hasChanges = () => {
    const currentData = JSON.stringify(
      schedules.map((s) => ({
        startDate: s.startDate,
        endDate: s.endDate,
        rules: s.rules.map((rule) => [rule.locationGroupId, rule.value]),
      })),
    )
    return currentData !== initialSchedulesRef.current
  }

  const handleClose = () => {
    if (hasChanges() && !success) {
      setShowConfirmDialog(true)
    } else {
      onClose()
    }
  }

  const handleConfirmDiscard = () => {
    setShowConfirmDialog(false)
    onClose()
  }

  const handleContinueEditing = () => {
    setShowConfirmDialog(false)
  }

  const handleAddSchedule = () => {
    setSchedules([
      ...schedules,
      {
        id: generateId(),
        startDate: '',
        endDate: '',
        rules: [{ id: generateId(), locationGroupId: null, value: '' }],
      },
    ])
  }

  const handleDeleteSchedule = (id: string) => {
    setSchedules(schedules.filter((s) => s.id !== id))
  }

  const handleScheduleChange = (
    id: string,
    field: 'startDate' | 'endDate',
    value: string,
  ) => {
    setSchedules(
      schedules.map((s) => (s.id === id ? { ...s, [field]: value } : s)),
    )
  }

  const handleRulesChange = (id: string, rules: IEditableRule[]) => {
    setSchedules(schedules.map((s) => (s.id === id ? { ...s, rules } : s)))
  }

  const validateSchedules = (): string | null => {
    for (const schedule of schedules) {
      if (!schedule.startDate) {
        return 'Start date is required for all schedules'
      }
      if (!schedule.endDate) {
        return 'End date is required for all schedules'
      }
      if (new Date(schedule.startDate) > new Date(schedule.endDate)) {
        return 'Start date cannot be after end date'
      }
      if (schedule.rules.length === 0) {
        return 'Every schedule needs at least one rule'
      }
      const rulesError = validateRules(schedule.rules)
      if (rulesError) {
        return rulesError
      }
    }

    // Check for duplicate date ranges
    for (let i = 0; i < schedules.length; i++) {
      for (let j = i + 1; j < schedules.length; j++) {
        const a = schedules[i]
        const b = schedules[j]
        // Check if date ranges overlap
        const aStart = new Date(a.startDate)
        const aEnd = new Date(a.endDate)
        const bStart = new Date(b.startDate)
        const bEnd = new Date(b.endDate)

        if (aStart <= bEnd && bStart <= aEnd) {
          return `Duplicate or overlapping date ranges detected: ${a.startDate} - ${a.endDate} and ${b.startDate} - ${b.endDate}`
        }
      }
    }

    return null
  }

  const handleSave = async () => {
    const validationError = validateSchedules()
    if (validationError) {
      setError(validationError)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // why: each rule is stored as its own row carrying the schedule's window,
      // so the grouping shown in this dialog is flattened on the way out.
      const schedulesToSave: IScheduleInput[] = schedules.flatMap((s) =>
        s.rules.map((rule) => ({
          startDate: s.startDate,
          endDate: s.endDate,
          value: parseFloat(rule.value),
          locationGroupId: rule.locationGroupId,
        })),
      )

      await editPrepSettingsScheduleMutation({
        variables: {
          iName: settingName,
          iSchedules: schedulesToSave,
        },
      })

      setSuccess(true)

      if (onSuccess) {
        onSuccess()
      }

      setTimeout(() => {
        onClose()
        setSuccess(false)
        setError(null)
        setIsLoading(false)
      }, 1500)
    } catch (err) {
      setError('Failed to update. Please try again.')
      setIsLoading(false)
    }
  }

  return (
    <>
      <Dialog open={visible} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Edit Scheduled Prep Amount Changes</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography sx={{ mb: 2, fontSize: 13.5, color: 'text.secondary' }}>
              A schedule is the same rules list, wrapped in a date range. While
              it is active it replaces the standing increase for the locations
              it names.
            </Typography>

            {scopesLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {scopesError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Could not load the list of states. Reload the page before adding
                a rule.
              </Alert>
            )}

            {!scopesLoading &&
              !scopesError &&
              schedules.map((schedule) => (
                <Box
                  key={schedule.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 2,
                    mb: 1.5,
                    backgroundColor: 'grey.50',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      gap: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <TextField
                      type="date"
                      label="Start date"
                      value={schedule.startDate}
                      onChange={(e) =>
                        handleScheduleChange(
                          schedule.id,
                          'startDate',
                          e.target.value,
                        )
                      }
                      size="small"
                      disabled={isLoading || success}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      type="date"
                      label="End date"
                      value={schedule.endDate}
                      onChange={(e) =>
                        handleScheduleChange(
                          schedule.id,
                          'endDate',
                          e.target.value,
                        )
                      }
                      size="small"
                      disabled={isLoading || success}
                      InputLabelProps={{ shrink: true }}
                    />
                    <Box sx={{ flex: 1 }} />
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteSchedule(schedule.id)}
                      color="error"
                      disabled={isLoading || success}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <RuleRows
                    rules={schedule.rules}
                    scopes={scopes}
                    disabled={isLoading || success}
                    onChange={(rules) => handleRulesChange(schedule.id, rules)}
                  />
                </Box>
              ))}

            {!scopesLoading && !scopesError && schedules.length === 0 && (
              <Typography
                sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}
              >
                No schedules configured. Click "Add Schedule" to create one.
              </Typography>
            )}

            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddSchedule}
                size="small"
              >
                Add Schedule
              </Button>
            </Box>

            <Typography sx={{ mt: 2, fontSize: 12.5, color: 'text.secondary' }}>
              A dated schedule outranks the standing increase for the dates it
              covers. Most specific wins: single store &gt; state / region &gt;
              all locations.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Successfully updated
              </Alert>
            )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
                mt: 4,
              }}
            >
              <Button
                onClick={handleClose}
                variant="outlined"
                disabled={isLoading}
              >
                Close
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                disabled={success || isLoading || scopesLoading || scopesError}
              >
                Save
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={showConfirmDialog} onClose={handleContinueEditing}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Do you want to discard them or continue
            editing?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleContinueEditing} variant="outlined">
            Continue Editing
          </Button>
          <Button
            onClick={handleConfirmDiscard}
            variant="contained"
            color="error"
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default EditPrepSettingsScheduleModal
